import React, { useState } from 'react';
import axios from 'axios';
import SmsSegmentCounter from './SmsSegmentCounter';
import { inZone } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

// "Texts" on a customer card (SMS Phase 2): the inbound and outbound thread
// and the consent changes, from GET /api/customers/:id/sms. A property card
// shows its customer's thread; the server resolves the id to the root.
//
// Collapsed until opened, and loaded each time it is opened, so a list of 200
// cards costs no requests until someone looks and a reply that came in since
// shows on the next open.
//
// Under the thread, with canSend, a one-line box sends one text to this
// customer (POST /api/customers/:id/texts, kind 'direct'). The server applies
// every rule a send has (consent, texting live, the daily allowance) except
// quiet hours, and says why when it refuses. The sent text joins the thread
// at once; the customer's reply lands here and is emailed as any reply is.
const fmt = (iso, timezone) => (iso ? new Date(iso).toLocaleString('en-US', inZone(timezone, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})) : '');

const STATUS_LABELS = {
  pending: 'Waiting', sending: 'Sending', queued: 'Queued', sent: 'Sent', delivered: 'Delivered',
  undelivered: 'Not delivered', failed: 'Failed', received: 'Received', skipped: 'Skipped',
};
const KIND_LABELS = { bulk: 'Bulk text', arrival: 'Arrival alert', keyword_reply: 'Auto reply', inbound: 'Reply', welcome: 'Welcome', direct: 'Direct text', service_request: 'Service request', skip_notice: 'Skip notice' };
const EVENT_LABELS = { opt_in: 'Agreed to texts', opt_out: 'Opted out' };

function CustomerTexts({ customerId, canSend = false }) {
  const timezone = useCompanyTimezone();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${customerId}/sms`);
        setData(res.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load texts');
      }
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    setSendError('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers/${customerId}/texts`, { body: draft });
      setData((d) => (d ? { ...d, messages: [res.data.message, ...d.messages] } : d));
      setDraft('');
    } catch (err) {
      setSendError(err.response?.data?.error || 'The text could not be sent');
    } finally {
      setSending(false);
    }
  };

  // Messages and consent changes on one timeline, newest first.
  const items = data ? [
    ...data.messages.map((m) => ({ ...m, type: 'message' })),
    ...data.consentEvents.map((e) => ({ ...e, type: 'consent' })),
  ].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))) : [];

  return (
    <div className="customer-texts" style={{ marginTop: '8px' }}>
      <button type="button" className="btn btn-link btn-sm" onClick={toggle} aria-expanded={open}
        style={{ padding: 0, fontSize: '0.8125rem' }}>
        {open ? '▼' : '▶'} Texts
      </button>
      {open && (
        <div style={{ marginTop: '6px', fontSize: '0.8125rem' }}>
          {error && <p className="text-muted" role="alert">{error}</p>}
          {!error && !data && <p className="text-muted">Loading...</p>}
          {data && data.messages.length === 0 && <p className="text-muted">No texts yet</p>}
          {data && items.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '240px', overflowY: 'auto' }}>
              {items.map((item) => (item.type === 'message' ? (
                <li key={`m${item.id}`} style={{
                  margin: '4px 0', padding: '6px 8px', borderRadius: '8px', maxWidth: '90%',
                  marginLeft: item.direction === 'outbound' ? 'auto' : 0,
                  background: item.direction === 'outbound' ? 'var(--primary-light, #e3f2fd)' : 'var(--background-color, #f5f5f5)',
                }}>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.body}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {KIND_LABELS[item.kind] || item.kind} &middot; {STATUS_LABELS[item.status] || item.status} &middot; {fmt(item.createdAt, timezone)}
                  </div>
                </li>
              ) : (
                <li key={`e${item.id}`} className="text-muted" style={{ margin: '4px 0', fontSize: '0.75rem', textAlign: 'center' }}>
                  {EVENT_LABELS[item.event] || item.event} &middot; {fmt(item.createdAt, timezone)}{item.recordedBy ? ` by ${item.recordedBy}` : ''}
                </li>
              )))}
            </ul>
          )}
          {data && canSend && (
            <form onSubmit={send} style={{ marginTop: '6px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={1600}
                  placeholder="Text this customer..."
                  aria-label="Text this customer"
                  style={{ flex: 1, fontSize: '0.8125rem', padding: '4px 8px' }}
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !draft.trim()}>
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
              {draft && <SmsSegmentCounter text={draft} />}
              {sendError && <p role="alert" style={{ color: 'var(--error-color, #c62828)', margin: '4px 0 0' }}>{sendError}</p>}
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerTexts;
